import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  ButtonGroup,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PanToolIcon from "@mui/icons-material/PanTool";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

type ToolType = "rect" | "arrow" | "pen" | "blackout" | "pan";

interface Point {
  x: number;
  y: number;
}

interface Shape {
  id: string;
  type: ToolType;
  color: string;
  lineWidth: number;
  points?: Point[];
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
}

interface ImageAnnotationModalProps {
  open: boolean;
  imageSource: File | Blob | string | null;
  onClose: () => void;
  onSave: (annotatedFile: File) => void;
}

const COLORS = [
  { label: "Đỏ", value: "#EF4444" },
  { label: "Vàng", value: "#F59E0B" },
  { label: "Xanh lá", value: "#10B981" },
  { label: "Xanh dương", value: "#3B82F6" },
  { label: "Đen", value: "#111827" },
  { label: "Trắng", value: "#FFFFFF" },
];

const LINE_WIDTHS = [
  { label: "Mảnh", value: 3 },
  { label: "Vừa", value: 5 },
  { label: "Dày", value: 9 },
];

export const ImageAnnotationModal: React.FC<ImageAnnotationModalProps> = ({
  open,
  imageSource,
  onClose,
  onSave,
}) => {
  const [activeTool, setActiveTool] = useState<ToolType>("rect");
  const [selectedColor, setSelectedColor] = useState<string>("#EF4444");
  const [selectedWidth, setSelectedWidth] = useState<number>(5);
  const [zoom, setZoom] = useState<number>(1);

  const [shapes, setShapes] = useState<Shape[]>([]);
  const currentShapeRef = useRef<Shape | null>(null);
  const isDrawingRef = useRef<boolean>(false);

  // Pan / Dragging state
  const isPanningRef = useRef<boolean>(false);
  const panStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number }>({
    x: 0,
    y: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const baseImageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // State to hold display dimensions
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({
    width: 800,
    height: 500,
  });

  // Load base image safely
  useEffect(() => {
    if (!open || !imageSource) {
      baseImageRef.current = null;
      setShapes([]);
      setZoom(1);
      return;
    }

    let isMounted = true;

    const processImage = (srcUrl: string) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (!isMounted) return;
        baseImageRef.current = img;

        // Kích thước canvas cơ sở to và rộng rãi hơn
        const maxW = Math.min(window.innerWidth * 0.9, 1200);
        const maxH = Math.min(window.innerHeight * 0.72, 700);

        let w = img.naturalWidth || 900;
        let h = img.naturalHeight || 650;

        const ratio = Math.min(maxW / w, maxH / h, 1);
        const displayW = Math.max(350, Math.round(w * ratio));
        const displayH = Math.max(250, Math.round(h * ratio));

        setCanvasSize({ width: displayW, height: displayH });
        setShapes([]);
        setZoom(1);
      };
      img.src = srcUrl;
    };

    if (typeof imageSource === "string") {
      processImage(imageSource);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result && typeof e.target.result === "string") {
          processImage(e.target.result);
        }
      };
      reader.readAsDataURL(imageSource);
    }

    return () => {
      isMounted = false;
    };
  }, [open, imageSource]);

  // Lắng nghe sự kiện cuộn con lăn chuột (Mouse Wheel) để Zoom In / Zoom Out
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !open) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      // Lăn lên (deltaY < 0) -> Zoom In, Lăn xuống (deltaY > 0) -> Zoom Out
      const zoomStep = e.deltaY < 0 ? 0.15 : -0.15;
      setZoom((prev) => {
        const next = Math.max(0.4, Math.min(3.5, +(prev + zoomStep).toFixed(2)));
        return next;
      });
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [open]);

  // Helper vẽ shape lên context
  const drawShape = (ctx: CanvasRenderingContext2D, shape: Shape | null | undefined) => {
    if (!ctx || !shape || !shape.color) return;

    ctx.save();
    ctx.strokeStyle = shape.color;
    ctx.fillStyle = shape.color;
    ctx.lineWidth = shape.lineWidth || 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (shape.type === "pen" && shape.points && shape.points.length > 0) {
      if (shape.points.length === 1) {
        ctx.beginPath();
        ctx.arc(shape.points[0].x, shape.points[0].y, (shape.lineWidth || 4) / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(shape.points[0].x, shape.points[0].y);
        for (let i = 1; i < shape.points.length; i++) {
          ctx.lineTo(shape.points[i].x, shape.points[i].y);
        }
        ctx.stroke();
      }
    } else if (shape.type === "rect") {
      if (
        shape.startX !== undefined &&
        shape.startY !== undefined &&
        shape.endX !== undefined &&
        shape.endY !== undefined
      ) {
        const x = Math.min(shape.startX, shape.endX);
        const y = Math.min(shape.startY, shape.endY);
        const w = Math.abs(shape.endX - shape.startX);
        const h = Math.abs(shape.endY - shape.startY);

        ctx.strokeRect(x, y, w, h);
      }
    } else if (shape.type === "blackout") {
      if (
        shape.startX !== undefined &&
        shape.startY !== undefined &&
        shape.endX !== undefined &&
        shape.endY !== undefined
      ) {
        const x = Math.min(shape.startX, shape.endX);
        const y = Math.min(shape.startY, shape.endY);
        const w = Math.abs(shape.endX - shape.startX);
        const h = Math.abs(shape.endY - shape.startY);

        ctx.fillStyle = "#111827";
        ctx.fillRect(x, y, w, h);
      }
    } else if (shape.type === "arrow") {
      if (
        shape.startX !== undefined &&
        shape.startY !== undefined &&
        shape.endX !== undefined &&
        shape.endY !== undefined
      ) {
        const fromX = shape.startX;
        const fromY = shape.startY;
        const toX = shape.endX;
        const toY = shape.endY;

        const headlen = Math.max((shape.lineWidth || 4) * 3.5, 14);
        const angle = Math.atan2(toY - fromY, toX - fromX);

        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX, toY);
        ctx.stroke();

        // Arrow head
        ctx.beginPath();
        ctx.moveTo(toX, toY);
        ctx.lineTo(
          toX - headlen * Math.cos(angle - Math.PI / 6),
          toY - headlen * Math.sin(angle - Math.PI / 6),
        );
        ctx.lineTo(
          toX - headlen * Math.cos(angle + Math.PI / 6),
          toY - headlen * Math.sin(angle + Math.PI / 6),
        );
        ctx.closePath();
        ctx.fill();
      }
    }

    ctx.restore();
  };

  // Render canvas lại toàn bộ
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Vẽ ảnh nền
    if (baseImageRef.current) {
      try {
        ctx.drawImage(baseImageRef.current, 0, 0, canvas.width, canvas.height);
      } catch (e) {
        console.warn("Could not draw base image to canvas:", e);
      }
    }

    // Vẽ tất cả shape trong history
    shapes.forEach((s) => {
      if (s) drawShape(ctx, s);
    });

    // Vẽ shape đang thao tác
    if (currentShapeRef.current) {
      drawShape(ctx, currentShapeRef.current);
    }
  }, [shapes]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas, canvasSize]);

  // Lấy tọa độ tương đối trên canvas (tự động map chuẩn xác theo zoom)
  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();

    let clientX = 0;
    let clientY = 0;

    if ("touches" in e) {
      if (!e.touches || e.touches.length === 0) return { x: 0, y: 0 };
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const scaleX = canvas.width / (rect.width || 1);
    const scaleY = canvas.height / (rect.height || 1);

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const handlePointerDown = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    // Nếu đang ở tool Pan
    if (activeTool === "pan") {
      isPanningRef.current = true;
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      panStartRef.current = {
        x: clientX,
        y: clientY,
        scrollLeft: containerRef.current?.scrollLeft || 0,
        scrollTop: containerRef.current?.scrollTop || 0,
      };
      return;
    }

    const { x, y } = getCanvasCoords(e);
    isDrawingRef.current = true;

    if (activeTool === "pen") {
      currentShapeRef.current = {
        id: `${Date.now()}`,
        type: "pen",
        color: selectedColor,
        lineWidth: selectedWidth,
        points: [{ x, y }],
      };
    } else {
      currentShapeRef.current = {
        id: `${Date.now()}`,
        type: activeTool,
        color: selectedColor,
        lineWidth: selectedWidth,
        startX: x,
        startY: y,
        endX: x,
        endY: y,
      };
    }
    redrawCanvas();
  };

  const handlePointerMove = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    // Xử lý Pan / Cuộn vùng nhìn
    if (isPanningRef.current && containerRef.current) {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const deltaX = clientX - panStartRef.current.x;
      const deltaY = clientY - panStartRef.current.y;

      containerRef.current.scrollLeft = panStartRef.current.scrollLeft - deltaX;
      containerRef.current.scrollTop = panStartRef.current.scrollTop - deltaY;
      return;
    }

    if (!isDrawingRef.current || !currentShapeRef.current) return;
    const { x, y } = getCanvasCoords(e);

    if (activeTool === "pen") {
      currentShapeRef.current.points = [
        ...(currentShapeRef.current.points || []),
        { x, y },
      ];
    } else {
      currentShapeRef.current.endX = x;
      currentShapeRef.current.endY = y;
    }
    redrawCanvas();
  };

  const handlePointerUp = () => {
    if (isPanningRef.current) {
      isPanningRef.current = false;
      return;
    }

    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;

    if (currentShapeRef.current) {
      const finishedShape = { ...currentShapeRef.current };
      setShapes((prev) => [...prev.filter(Boolean), finishedShape]);
      currentShapeRef.current = null;
    }
    redrawCanvas();
  };

  // Zoom controls
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(3.5, +(prev + 0.25).toFixed(2)));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(0.4, +(prev - 0.25).toFixed(2)));
  };

  const handleResetZoom = () => {
    setZoom(1);
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  };

  // Thao tác Undo & Clear
  const handleUndo = () => {
    setShapes((prev) => prev.slice(0, -1));
  };

  const handleClearAll = () => {
    setShapes([]);
  };

  // Xuất file ảnh với độ phân giải gốc của ảnh ban đầu
  const handleSave = () => {
    if (!baseImageRef.current) return;

    const baseImg = baseImageRef.current;
    const naturalW = baseImg.naturalWidth || canvasSize.width;
    const naturalH = baseImg.naturalHeight || canvasSize.height;

    const offscreen = document.createElement("canvas");
    offscreen.width = naturalW;
    offscreen.height = naturalH;
    const offCtx = offscreen.getContext("2d");
    if (!offCtx) return;

    // Vẽ base image ở độ phân giải gốc
    offCtx.drawImage(baseImg, 0, 0, naturalW, naturalH);

    // Tỉ lệ scale từ display canvas -> natural canvas
    const scaleX = naturalW / (canvasSize.width || 1);
    const scaleY = naturalH / (canvasSize.height || 1);
    const scaleAvg = (scaleX + scaleY) / 2;

    shapes.filter(Boolean).forEach((s) => {
      const scaledShape: Shape = {
        ...s,
        lineWidth: (s.lineWidth || 4) * scaleAvg,
        startX: s.startX !== undefined ? s.startX * scaleX : undefined,
        startY: s.startY !== undefined ? s.startY * scaleY : undefined,
        endX: s.endX !== undefined ? s.endX * scaleX : undefined,
        endY: s.endY !== undefined ? s.endY * scaleY : undefined,
        points: s.points?.map((p) => ({
          x: p.x * scaleX,
          y: p.y * scaleY,
        })),
      };
      drawShape(offCtx, scaledShape);
    });

    offscreen.toBlob((blob) => {
      if (blob) {
        const file = new File(
          [blob],
          `annotated-screenshot-${Date.now()}.png`,
          { type: "image/png" },
        );
        onSave(file);
        onClose();
      }
    }, "image/png");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: "#1E293B",
          color: "#F8FAFC",
          width: { xs: "96vw", md: "92vw", lg: "88vw" },
          maxWidth: "1280px",
          height: { xs: "92vh", md: "88vh" },
          maxHeight: "920px",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <EditIcon sx={{ color: "#38BDF8" }} />
          <Typography variant="h6" fontWeight={700} color="#F8FAFC">
            Khoanh Vùng & Chú Thích Lỗi
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#94A3B8" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, alignItems: "center", overflow: "hidden" }}>
        {/* Toolbar công cụ */}
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap={1.5}
          p={1.5}
          borderRadius={2}
          sx={{ backgroundColor: "#0F172A", border: "1px solid rgba(255, 255, 255, 0.08)" }}
        >
          {/* Lựa chọn công cụ */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <ButtonGroup size="small" variant="outlined">
              <Tooltip title="Khoanh hộp chữ nhật">
                <Button
                  variant={activeTool === "rect" ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setActiveTool("rect")}
                  startIcon={<CropSquareIcon />}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Hộp
                </Button>
              </Tooltip>

              <Tooltip title="Vẽ mũi tên chỉ điểm">
                <Button
                  variant={activeTool === "arrow" ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setActiveTool("arrow")}
                  startIcon={<ArrowForwardIcon />}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Mũi tên
                </Button>
              </Tooltip>

              <Tooltip title="Bút vẽ tự do">
                <Button
                  variant={activeTool === "pen" ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setActiveTool("pen")}
                  startIcon={<EditIcon />}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Bút vẽ
                </Button>
              </Tooltip>

              <Tooltip title="Hộp đen che thông tin nhạy cảm">
                <Button
                  variant={activeTool === "blackout" ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setActiveTool("blackout")}
                  startIcon={<VisibilityOffIcon />}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Che tin
                </Button>
              </Tooltip>

              <Tooltip title="Di chuyển / Kéo rê vùng nhìn ảnh">
                <Button
                  variant={activeTool === "pan" ? "contained" : "outlined"}
                  color="secondary"
                  onClick={() => setActiveTool("pan")}
                  startIcon={<PanToolIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: activeTool === "pan" ? "#475569" : "transparent",
                    color: "#F8FAFC",
                    borderColor: "rgba(255,255,255,0.2)",
                  }}
                >
                  Di chuyển
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>

          {/* Chọn màu sắc (ẩn khi đang ở tool che tin hoặc pan) */}
          {activeTool !== "blackout" && activeTool !== "pan" && (
            <Box display="flex" alignItems="center" gap={0.8}>
              <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600 }}>
                Màu:
              </Typography>
              {COLORS.map((c) => (
                <Box
                  key={c.value}
                  onClick={() => setSelectedColor(c.value)}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    backgroundColor: c.value,
                    cursor: "pointer",
                    border: selectedColor === c.value ? "2px solid #38BDF8" : "1px solid rgba(255, 255, 255, 0.3)",
                    transform: selectedColor === c.value ? "scale(1.2)" : "scale(1)",
                    transition: "transform 0.15s",
                  }}
                />
              ))}
            </Box>
          )}

          {/* Chọn kích cỡ nét */}
          {activeTool !== "pan" && (
            <Box display="flex" alignItems="center" gap={0.8}>
              <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600 }}>
                Nét:
              </Typography>
              {LINE_WIDTHS.map((lw) => (
                <Button
                  key={lw.value}
                  size="small"
                  variant={selectedWidth === lw.value ? "contained" : "text"}
                  onClick={() => setSelectedWidth(lw.value)}
                  sx={{
                    minWidth: 32,
                    height: 26,
                    p: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    color: selectedWidth === lw.value ? "#FFFFFF" : "#94A3B8",
                    backgroundColor: selectedWidth === lw.value ? "#334155" : "transparent",
                  }}
                >
                  {lw.label}
                </Button>
              ))}
            </Box>
          )}

          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.1)" }} />

          {/* Zoom Controls & Mouse Scroll Hint */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Thu nhỏ (-25%) hoặc cuộn chuột xuống">
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.4}
                  sx={{ color: zoom > 0.4 ? "#F8FAFC" : "#64748B" }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Cuộn chuột để Phóng to / Thu nhỏ nhanh">
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "#38BDF8",
                  minWidth: 42,
                  textAlign: "center",
                  userSelect: "none",
                  cursor: "pointer",
                }}
              >
                {Math.round(zoom * 100)}%
              </Typography>
            </Tooltip>

            <Tooltip title="Phóng to (+25%) hoặc cuộn chuột lên">
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  disabled={zoom >= 3.5}
                  sx={{ color: zoom < 3.5 ? "#F8FAFC" : "#64748B" }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Khôi phục kích thước 100%">
              <span>
                <IconButton
                  size="small"
                  onClick={handleResetZoom}
                  disabled={zoom === 1}
                  sx={{ color: zoom !== 1 ? "#F8FAFC" : "#64748B" }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.1)" }} />

          {/* Undo & Clear */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Hoàn tác nét vẽ vừa tạo">
              <span>
                <IconButton
                  size="small"
                  onClick={handleUndo}
                  disabled={shapes.length === 0}
                  sx={{ color: shapes.length > 0 ? "#F8FAFC" : "#64748B" }}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Xóa toàn bộ chú thích">
              <span>
                <IconButton
                  size="small"
                  onClick={handleClearAll}
                  disabled={shapes.length === 0}
                  sx={{ color: shapes.length > 0 ? "#EF4444" : "#64748B" }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Khung Canvas hiển thị cuộn, zoom lăn chuột & pan */}
        <Box
          ref={containerRef}
          sx={{
            width: "100%",
            flex: 1,
            minHeight: "420px",
            maxHeight: "72vh",
            display: "flex",
            justifyContent: zoom <= 1 ? "center" : "flex-start",
            alignItems: zoom <= 1 ? "center" : "flex-start",
            overflow: "auto",
            backgroundColor: "#090D16",
            borderRadius: 2,
            p: 2,
            border: "1px solid rgba(255, 255, 255, 0.05)",
            userSelect: "none",
            cursor: activeTool === "pan" ? (isPanningRef.current ? "grabbing" : "grab") : "default",
          }}
        >
          <Box
            sx={{
              display: "inline-block",
              width: `${Math.round(canvasSize.width * zoom)}px`,
              height: `${Math.round(canvasSize.height * zoom)}px`,
              flexShrink: 0,
              transition: "width 0.1s ease-out, height 0.1s ease-out",
            }}
          >
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              style={{
                width: "100%",
                height: "100%",
                cursor: activeTool === "pan" ? (isPanningRef.current ? "grabbing" : "grab") : "crosshair",
                borderRadius: 6,
                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                touchAction: "none",
                display: "block",
              }}
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerUp}
              onTouchStart={handlePointerDown}
              onTouchMove={handlePointerMove}
              onTouchEnd={handlePointerUp}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}>
        <Button onClick={onClose} sx={{ color: "#94A3B8", textTransform: "none", fontWeight: 600 }}>
          Hủy
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          startIcon={<CheckIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            backgroundColor: "#0D9488",
            "&:hover": { backgroundColor: "#0F766E" },
          }}
        >
          Xong & Thêm vào báo cáo
        </Button>
      </DialogActions>
    </Dialog>
  );
};
